import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {SpotlightAction} from "@mantine/spotlight";
import {SpotlightAction as MantineSpotlightAction} from "@mantine/spotlight/lib/types";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";
import {Entity} from "@ots-frontend/ots-data";
import {getEntityTypeDisplayValueAsString} from "../../util/entity-ui-util";

export type UseEntitySpotlightActionProps<E extends Entity> = {
  queryPrefix: string,
  query?: string
  entities?: E[],
  permissions?: string[]
  onTrigger: (e: E) => void
}

export type UseEntitySpotlightActionResult = {
  actions: SpotlightAction[]
}

export function useEntitySpotlightAction<E extends Entity>(props: UseEntitySpotlightActionProps<E>): UseEntitySpotlightActionResult {
  const {
    queryPrefix,
    query,
    entities,
    permissions,
    onTrigger
  } = props

  const {hasAccess} = useAuth()

  const {t} = useTranslation(["common"])

  const _actions: MantineSpotlightAction[] = useMemo(() => {
    if (!entities) {
      return []
    }

    const entityKeyword = `${queryPrefix}://`
    if (!query || !query.startsWith(entityKeyword)) {
      return [];
    }

    const entityQuery = query.replace(entityKeyword, "")
    if (!entityQuery) {
      return []
    }

    if (permissions && !hasAccess(...permissions)) {
      return []
    }

    const filteredEntities = entities.filter((e) => {
      return e.displayName && e.displayName.toLowerCase().includes(entityQuery.toLowerCase())
    })

    return filteredEntities.map((e) => ({
      title: e.displayName,
      description: getEntityTypeDisplayValueAsString(e.entityType, t),
      onTrigger: () => {
        onTrigger(e)
      },
    }))

  }, [hasAccess, permissions, queryPrefix, query, entities, t, onTrigger]);

  return {
    actions: _actions
  }
}
